var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import classNames from 'classnames';
import useTaroBaseEvents from '../_util/hooks/useTaroBaseEvents';
import { createTaroVideoEvent } from '../_util/taroEvent';
const Video = (_a, ref) => {
    var { id, className, style, src, initialTime, controls = true, autoplay = false, loop = false, muted = false, objectFit = 'contain', poster, onPlay, onPause, onEnded, onTimeUpdate, onFullscreenChange, onWaiting, onError, onLoadedMetaData, 
    // unsupported props
    title, pageGesture, direction, showProgress, showFullscreenBtn, enableProgressGesture, danmuList, enableDanmu, showPlayBtn, showCenterPlayBtn, showMuteBtn, showNoWifiTip, vslideGesture, vslideGestureInFullscreen, showLockBtn, enablePlayGesture } = _a, rest = __rest(_a, ["id", "className", "style", "src", "initialTime", "controls", "autoplay", "loop", "muted", "objectFit", "poster", "onPlay", "onPause", "onEnded", "onTimeUpdate", "onFullscreenChange", "onWaiting", "onError", "onLoadedMetaData", "title", "pageGesture", "direction", "showProgress", "showFullscreenBtn", "enableProgressGesture", "danmuList", "enableDanmu", "showPlayBtn", "showCenterPlayBtn", "showMuteBtn", "showNoWifiTip", "vslideGesture", "vslideGestureInFullscreen", "showLockBtn", "enablePlayGesture"]);
    const props = useTaroBaseEvents(rest);
    const isFirstPaly = useRef(true);
    const video = useRef(null);
    useImperativeHandle(ref, () => video.current);
    useEffect(() => {
        if (!video.current || !onFullscreenChange) {
            return;
        }
        function handle(event) {
            let fullscreen = '0';
            if ('webkitFullscreenElement' in document) {
                fullscreen = document.webkitFullscreenElement === video.current ? '1' : '0';
            }
            else {
                fullscreen = document.fullscreenElement === video.current ? '1' : '0';
            }
            const { timeStamp, target, currentTarget } = event;
            const taroEvent = {
                type: 'fullscreenchange',
                detail: {
                    videoId: id,
                    fullscreen
                },
                timeStamp,
                target,
                currentTarget,
                preventDefault: () => event.preventDefault(),
                stopPropagation: () => event.stopPropagation()
            };
            onFullscreenChange === null || onFullscreenChange === void 0 ? void 0 : onFullscreenChange(taroEvent);
        }
        const videoEl = video.current;
        videoEl.addEventListener('webkitfullscreenchange', handle);
        videoEl.addEventListener('fullscreenchange', handle);
        return () => {
            videoEl.removeEventListener('webkitfullscreenchange', handle);
            videoEl.removeEventListener('fullscreenchange', handle);
        };
    }, [id, onFullscreenChange]);
    const mergedStyle = Object.assign({
        objectFit
    }, style);
    return (_jsx("video", Object.assign({ ref: video, id: id, className: classNames('video-js', className), style: mergedStyle, playsInline: true, src: src, controls: controls, autoPlay: autoplay, loop: loop, muted: muted, poster: poster, onPlay: event => {
            if (video.current && isFirstPaly.current && initialTime) {
                isFirstPaly.current = false;
                video.current.currentTime = initialTime;
            }
            if (onPlay) {
                const taroEvent = createTaroVideoEvent('play', event);
                onPlay(taroEvent);
            }
        }, onPause: event => {
            if (onPause) {
                const taroEvent = createTaroVideoEvent('pause', event);
                onPause(taroEvent);
            }
        }, onEnded: event => {
            if (onEnded) {
                const taroEvent = createTaroVideoEvent('ended', event);
                onEnded(taroEvent);
            }
        }, onTimeUpdate: event => {
            if (onTimeUpdate) {
                const videoEl = video.current;
                const { timeStamp, target, currentTarget, preventDefault, stopPropagation } = event;
                const taroEvent = {
                    type: 'timeupdate',
                    detail: {
                        videoId: id,
                        currentTime: videoEl.currentTime,
                        duration: videoEl.duration
                    },
                    timeStamp,
                    target,
                    currentTarget,
                    preventDefault,
                    stopPropagation
                };
                onTimeUpdate(taroEvent);
            }
        }, onWaiting: event => {
            if (onWaiting) {
                const taroEvent = createTaroVideoEvent('waiting', event);
                onWaiting(taroEvent);
            }
        }, onError: event => {
            if (onError) {
                const taroEvent = createTaroVideoEvent('error', event);
                onError(taroEvent);
            }
        }, onLoadedMetadata: event => {
            if (onLoadedMetaData) {
                const videoEl = video.current;
                const { timeStamp, target, currentTarget } = event;
                const taroEvent = {
                    type: 'loadedmetadata',
                    detail: {
                        videoId: id,
                        duration: videoEl.duration,
                        height: videoEl.videoHeight,
                        width: videoEl.videoWidth
                    },
                    timeStamp,
                    target,
                    currentTarget,
                    preventDefault: () => event.preventDefault(),
                    stopPropagation: () => event.stopPropagation()
                };
                onLoadedMetaData(taroEvent);
            }
        } }, props)));
};
export default forwardRef(Video);
